import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle,
  Error as ErrorIcon,
  Info as InfoIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface DomainConfig {
  providerId?: string;
  clientId?: string;
  clientSecret?: string;
  apiUrl?: string;
  redirectUrl?: string;
  scopes?: string[];
}

interface DomainStatus {
  configured: boolean;
  providerIdSet: boolean;
  clientIdSet: boolean;
  clientSecretSet: boolean;
  redirectUrl: string;
  scopes: string[];
  source?: 'database' | 'environment';
}

const AdminDomainIntegration = () => {
  const [config, setConfig] = useState<DomainConfig>({});
  const [status, setStatus] = useState<DomainStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/domain-config');
      setConfig(response.data.config || {});
      setStatus(response.data.status || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load Domain.com.au configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof DomainConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfig(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);
      
      // Client ID is required, Client Secret is required only if not already set
      if (!config.clientId) {
        setError('Client ID is required');
        return;
      }
      
      // If client secret is not set in status, it's required
      if (!status?.clientSecretSet && !config.clientSecret) {
        setError('Client Secret is required');
        return;
      }
      
      // If client secret is already set but user wants to update it, they must provide new value
      // If they leave it empty, we won't update it (backend handles this)

      await api.put('/admin/domain-config', {
        providerId: config.providerId,
        clientId: config.clientId,
        clientSecret: config.clientSecret,
        apiUrl: config.apiUrl,
      });

      setSuccess(true);
      await fetchConfig(); // Refresh status
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError(null);
      const response = await api.post('/admin/domain-config/test');
      if (response.data.success) {
        setSuccess(true);
        setError(null);
      } else {
        setError(response.data.message || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading Domain.com.au configuration...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Domain.com.au Integration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure system-wide Domain.com.au Partner API credentials. These credentials are used for all agency OAuth connections.
        </Typography>

        {/* Status Card */}
        {status && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Configuration Status
                </Typography>
                <Chip
                  icon={status.configured ? <CheckCircle /> : <ErrorIcon />}
                  label={status.configured ? 'Configured' : 'Not Configured'}
                  color={status.configured ? 'success' : 'error'}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Provider ID
                  </Typography>
                  <Chip
                    label={status.providerIdSet ? 'Set' : 'Not Set'}
                    color={status.providerIdSet ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    API Key (Client ID)
                  </Typography>
                  <Chip
                    label={status.clientIdSet ? 'Set' : 'Not Set'}
                    color={status.clientIdSet ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Client Secret
                  </Typography>
                  <Chip
                    label={status.clientSecretSet ? 'Set' : 'Not Set'}
                    color={status.clientSecretSet ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Redirect URL
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {status.redirectUrl}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Register this URL with Domain.com.au in your Partner API settings
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Required Scopes
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {status.scopes.map((scope, index) => (
                      <Chip
                        key={index}
                        label={scope}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Configuration Form */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6">
              Domain.com.au Partner API Credentials
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Note:</strong> These credentials are provided by Domain.com.au when you register Workflow.Estate as a CRM Partner.
              You can set them here (stored in database) or via environment variables. Database settings take precedence over environment variables.
            </Typography>
            {status?.source && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Current source:</strong> {status.source === 'database' ? 'Database (SystemSettings)' : 'Environment Variables'}
              </Typography>
            )}
          </Alert>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Provider ID"
                  value={config.providerId || ''}
                  onChange={handleChange('providerId')}
                  helperText="Provider ID given by Domain.com.au (identifies Workflow.Estate as a CRM)"
                  placeholder="e.g., WFESTATE-001"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Key (Client ID)"
                  value={config.clientId || ''}
                  onChange={handleChange('clientId')}
                  helperText={status?.clientIdSet ? "Enter new Client ID to update (current value is masked)" : "OAuth2 Client ID / API Key provided by Domain"}
                  placeholder={status?.clientIdSet ? "Enter new Client ID" : "Your API Key"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Client Secret"
                  value={config.clientSecret || ''}
                  onChange={handleChange('clientSecret')}
                  helperText="OAuth2 Client Secret provided by Domain (leave blank to keep existing)"
                  placeholder={status?.clientSecretSet ? "Enter new secret to update" : "Your Client Secret"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API URL"
                  value={config.apiUrl || 'https://api.domain.com.au/v1'}
                  onChange={handleChange('apiUrl')}
                  helperText="Domain.com.au API endpoint URL"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={testing || !status?.configured}
                    startIcon={<LinkIcon />}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Documentation Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Setup Instructions
            </Typography>
            <Box component="ol" sx={{ pl: 3 }}>
              <li>
                <Typography variant="body2" paragraph>
                  <strong>Contact Domain.com.au</strong> to register Workflow.Estate as a CRM Partner
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  <strong>Provide Domain with:</strong>
                  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                    <li>Company name: Workflow.Estate</li>
                    <li>Redirect URL: <code>{status?.redirectUrl || 'N/A'}</code></li>
                    <li>Required scopes: <code>property.write</code>, <code>office.read</code>, <code>property.read</code></li>
                  </Box>
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  <strong>Receive from Domain:</strong>
                  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                    <li>Provider ID</li>
                    <li>API Key (Client ID)</li>
                    <li>Client Secret</li>
                  </Box>
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  <strong>Configure environment variables:</strong>
                  <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 1, fontSize: '0.875rem' }}>
{`DOMAIN_PROVIDER_ID=your_provider_id
DOMAIN_CLIENT_ID=your_api_key
DOMAIN_CLIENT_SECRET=your_client_secret
DOMAIN_API_URL=https://api.domain.com.au/v1
FRONTEND_URL=https://app.workflow.estate`}
                  </Box>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Test the connection</strong> using the "Test Connection" button above
                </Typography>
              </li>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Link
                href="https://developer.domain.com.au/docs/introduction"
                target="_blank"
                rel="noopener noreferrer"
              >
                Domain.com.au Developer Documentation
              </Link>
            </Box>
          </CardContent>
        </Card>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Success Snackbar */}
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Configuration saved successfully
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AdminDomainIntegration;

